/**
 * ============================================================================
 * Iron Meridian - Globe Render Contract
 * ============================================================================
 * 
 * INVARIANTS - The Globe Rendering Contract:
 * 
 * 1. STRUCTURE IMMUTABILITY
 *    - Each SVG <path> representing a country MUST have a valid ISO-3 id
 *    - Country paths are created ONCE during initial render
 *    - Paths are NEVER re-created during rotation, pan, or zoom
 *    - Updates occur ONLY via transform, style, or 'd' attribute changes
 * 
 * 2. ID UNIQUENESS
 *    - Each country ID appears exactly ONCE in the SVG DOM
 *    - Duplicate paths are a HARD ERROR in development
 *    - Missing expected paths are a HARD ERROR in development
 * 
 * 3. STABILITY GUARANTEES
 *    - The initial SVG structure hash is computed on first render
 *    - Subsequent renders MUST match the initial structure
 *    - Structure = set of country IDs (order-independent)
 * 
 * Usage:
 *   const contract = createGlobeRenderContract();
 *   contract.initialize(svgElement);  // Call once after initial render
 *   contract.verify();                // Call after each rotation/update
 * 
 * This module is the guardian of globe rendering integrity.
 * Treat it as immutable infrastructure - do not bypass these checks.
 */

// Environment detection
const isDevelopment = () => {
  if (typeof process !== 'undefined' && process.env) {
    return process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
  }
  // Browser environment - check for localhost or dev indicators
  if (typeof window !== 'undefined') {
    return window.location?.hostname === 'localhost' || 
           window.location?.hostname === '127.0.0.1' ||
           window.__IRON_MERIDIAN_DEV__ === true;
  }
  return false;
};

/**
 * Compute a deterministic hash of country IDs for structure comparison
 * @param {string[]} countryIds - Array of ISO-3 country codes
 * @returns {string} Hex hash of sorted country IDs
 */
function computeStructureHash(countryIds) {
  const sorted = [...countryIds].sort();
  const str = sorted.join('|');
  
  // Simple hash function (djb2 variant)
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Validate ISO-3 country code format
 * @param {string} id - Country ID to validate
 * @returns {boolean} True if valid ISO-3 format
 */
function isValidIso3(id) {
  if (!id || typeof id !== 'string') return false;
  // ISO-3 codes are exactly 3 uppercase letters, or special cases like 'UNK' prefixed codes
  return /^[A-Z]{3}$/.test(id) || /^UNK\d+$/.test(id);
}

/**
 * Contract violation error for development mode
 */
class GlobeContractViolation extends Error {
  constructor(message, details = {}) {
    super(`[Globe Contract Violation] ${message}`);
    this.name = 'GlobeContractViolation';
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

/**
 * Create a new Globe Render Contract instance
 * @returns {GlobeRenderContract} Contract instance
 */
export function createGlobeRenderContract() {
  // Contract state
  let initialized = false;
  let initialStructureHash = null;
  let initialCountryIds = null;
  let countriesGroupRef = null;
  let verificationCount = 0;
  let violations = [];

  /**
   * Extract country IDs from the SVG countries group
   * @param {Element} countriesGroup - SVG <g> element containing country paths
   * @returns {string[]} Array of country IDs
   */
  function extractCountryIds(countriesGroup) {
    if (!countriesGroup) {
      throw new GlobeContractViolation('Countries group element is null or undefined');
    }

    const paths = countriesGroup.querySelectorAll('path[data-country-id]');
    const ids = [];
    const seen = new Set();
    const duplicates = [];
    const invalid = [];

    paths.forEach(path => {
      const id = path.getAttribute('data-country-id');
      
      // Check for valid ISO-3 format
      if (!isValidIso3(id)) {
        invalid.push(id);
      }
      
      // Check for duplicates
      if (seen.has(id)) {
        duplicates.push(id);
      } else {
        seen.add(id);
        ids.push(id);
      }
    });

    return { ids, duplicates, invalid };
  }

  /**
   * Log a violation for telemetry
   * @param {string} type - Violation type
   * @param {object} details - Violation details
   */
  function logViolation(type, details) {
    const violation = {
      type,
      details,
      timestamp: new Date().toISOString(),
      verificationCount
    };
    violations.push(violation);
    
    // Structured logging for telemetry
    console.warn('[Globe Contract]', JSON.stringify(violation));
  }

  /**
   * Handle a contract violation based on environment
   * @param {string} message - Error message
   * @param {object} details - Error details
   */
  function handleViolation(message, details) {
    logViolation(message, details);

    if (isDevelopment()) {
      throw new GlobeContractViolation(message, details);
    } else {
      // Production: Log structured warning for telemetry, continue gracefully
      console.error(`[Globe Contract Warning] ${message}`, details);
    }
  }

  return {
    /**
     * Initialize the contract with the current SVG state
     * This captures the baseline structure that must be maintained
     * @param {Element} countriesGroup - SVG <g> element containing country paths
     */
    initialize(countriesGroup) {
      if (initialized) {
        console.warn('[Globe Contract] Already initialized. Use verify() for subsequent checks.');
        return;
      }

      if (!countriesGroup) {
        handleViolation('Cannot initialize: countries group is null', {});
        return;
      }

      countriesGroupRef = countriesGroup;
      const { ids, duplicates, invalid } = extractCountryIds(countriesGroup);

      // Check for duplicates during initialization
      if (duplicates.length > 0) {
        handleViolation('Duplicate country paths detected during initialization', {
          duplicates,
          count: duplicates.length
        });
      }

      // Check for invalid IDs
      if (invalid.length > 0) {
        handleViolation('Invalid ISO-3 country IDs detected', {
          invalid,
          count: invalid.length
        });
      }

      initialCountryIds = new Set(ids);
      initialStructureHash = computeStructureHash(ids);
      initialized = true;

      console.log(`[Globe Contract] Initialized with ${ids.length} countries. Hash: ${initialStructureHash}`);
    },

    /**
     * Verify the current SVG state matches the initial contract
     * Call this after rotation, pan, or any update that should NOT change structure
     * @returns {VerificationResult} Result object with pass/fail and details
     */
    verify() {
      verificationCount++;

      if (!initialized) {
        const result = {
          passed: false,
          error: 'Contract not initialized',
          verificationCount
        };
        console.warn('[Globe Contract]', result);
        return result;
      }

      if (!countriesGroupRef) {
        handleViolation('Countries group reference lost', {});
        return { passed: false, error: 'Countries group reference lost' };
      }

      const { ids, duplicates, invalid } = extractCountryIds(countriesGroupRef);
      const currentHash = computeStructureHash(ids);
      const currentIds = new Set(ids);

      // Check for structure hash mismatch
      if (currentHash !== initialStructureHash) {
        const added = ids.filter(id => !initialCountryIds.has(id));
        const removed = [...initialCountryIds].filter(id => !currentIds.has(id));
        
        handleViolation('SVG structure changed - paths were added or removed', {
          expectedHash: initialStructureHash,
          actualHash: currentHash,
          added,
          removed,
          addedCount: added.length,
          removedCount: removed.length
        });

        return {
          passed: false,
          error: 'Structure mismatch',
          added,
          removed
        };
      }

      // Check for duplicates (shouldn't happen if hash matches, but be defensive)
      if (duplicates.length > 0) {
        handleViolation('Duplicate country paths detected', {
          duplicates,
          count: duplicates.length
        });
        return { passed: false, error: 'Duplicate paths', duplicates };
      }

      return {
        passed: true,
        verificationCount,
        countryCount: ids.length,
        hash: currentHash
      };
    },

    /**
     * Get contract status for diagnostics
     * @returns {object} Current contract state
     */
    getStatus() {
      return {
        initialized,
        structureHash: initialStructureHash,
        countryCount: initialCountryIds?.size ?? 0,
        verificationCount,
        violationCount: violations.length,
        violations: [...violations]
      };
    },

    /**
     * Get the list of country IDs tracked by this contract
     * @returns {string[]} Array of ISO-3 country codes
     */
    getTrackedCountries() {
      return initialCountryIds ? [...initialCountryIds] : [];
    },

    /**
     * Reset the contract (useful for testing)
     */
    reset() {
      initialized = false;
      initialStructureHash = null;
      initialCountryIds = null;
      countriesGroupRef = null;
      verificationCount = 0;
      violations = [];
    },

    /**
     * Check if a specific country ID is tracked
     * @param {string} countryId - ISO-3 country code
     * @returns {boolean} True if country is in the contract
     */
    hasCountry(countryId) {
      return initialCountryIds?.has(countryId) ?? false;
    }
  };
}

/**
 * Singleton contract instance for global access
 * Use this for the main globe component
 */
let globalContract = null;

export function getGlobalGlobeContract() {
  if (!globalContract) {
    globalContract = createGlobeRenderContract();
  }
  return globalContract;
}

export function resetGlobalGlobeContract() {
  if (globalContract) {
    globalContract.reset();
  }
  globalContract = null;
}

// Export utilities for testing
export const _internals = {
  computeStructureHash,
  isValidIso3,
  isDevelopment,
  GlobeContractViolation
};

export default {
  createGlobeRenderContract,
  getGlobalGlobeContract,
  resetGlobalGlobeContract
};
